import {
  CatalogEntry,
  DesignElement,
  ElementRender,
  Placeholder,
} from '../../../services/voucher-design.service';
import { fillerText } from './sample-text';

/**
 * Placeholders in a text's content: `{code}`, `{company_city}`; `{{` and `}}` are
 * literal braces. Mirror of the backend's voucher_design/placeholders.py; keep the
 * two in step. The editor is lenient where the backend refuses (an unknown key or
 * a stray brace stays literal here and `contentProblem` names it), so a half-typed
 * content never breaks the canvas.
 */
export type Run = { kind: 'text'; value: string } | { kind: 'placeholder'; key: string };

/** The address as one line, a ready-made text of its parts (the backend's ADDRESS_LINE). */
export const ADDRESS_LINE = '{company_street} · {company_zip} {company_city} · {company_country}';
export type PlaceholderMap = ReadonlyMap<string, Placeholder>;

const TOKEN = /\{\{|\}\}|\{([a-z_]+)\}|[{}]/g;

/** The content as literal runs and placeholder runs, in order. */
export function tokenize(content: string, known: PlaceholderMap): Run[] {
  const runs: Run[] = [];
  let literal = '';
  let pos = 0;
  const flush = () => {
    if (literal) runs.push({ kind: 'text', value: literal });
    literal = '';
  };
  for (const match of content.matchAll(TOKEN)) {
    literal += content.slice(pos, match.index);
    const token = match[0];
    if (token === '{{') literal += '{';
    else if (token === '}}') literal += '}';
    else if (match[1] && known.has(match[1])) {
      flush();
      runs.push({ kind: 'placeholder', key: match[1] });
    } else literal += token;
    pos = match.index + token.length;
  }
  literal += content.slice(pos);
  flush();
  return runs;
}

/** Why the backend would refuse this content, as the editor's message, or null. */
export function contentProblem(content: string, known: PlaceholderMap): string | null {
  for (const match of content.matchAll(TOKEN)) {
    const token = match[0];
    if (token === '{{' || token === '}}') continue;
    if (match[1]) {
      if (!known.has(match[1])) return `Unbekannter Platzhalter {${match[1]}}`;
      continue;
    }
    return 'Eine einzelne geschweifte Klammer: für eine Klammer im Text {{ oder }} schreiben';
  }
  return null;
}

/** The placeholder keys a content uses, each once, in order of first use. */
export function placeholderKeys(content: string, known: PlaceholderMap): string[] {
  const keys: string[] = [];
  for (const run of tokenize(content, known)) {
    if (run.kind === 'placeholder' && !keys.includes(run.key)) keys.push(run.key);
  }
  return keys;
}

/** `text` as literal content: braces doubled. */
export function escape(text: string): string {
  return text.replace(/\{/g, '{{').replace(/\}/g, '}}');
}

/** The sample of a placeholder, at its planned length when `longTexts` asks for it. */
export function placeholderSample(placeholder: Placeholder, longTexts: boolean): string {
  const variable = placeholder.source !== 'company' && placeholder.content_max;
  return longTexts && variable ? fillerText(placeholder.content_max!) : placeholder.sample;
}

/** The content with every placeholder replaced by its sample: what the canvas shows. */
export function resolveSample(content: string, known: PlaceholderMap, longTexts = false): string {
  return tokenize(content, known)
    .map((run) =>
      run.kind === 'text' ? run.value : placeholderSample(known.get(run.key)!, longTexts),
    )
    .join('');
}

/** The content with every placeholder at its longest, for the fit warning (the
 * backend's planned_text): sample prose up to its content_max, else the sample. */
export function plannedText(content: string, known: PlaceholderMap): string {
  return tokenize(content, known)
    .map((run) => {
      if (run.kind === 'text') return run.value;
      const placeholder = known.get(run.key)!;
      return placeholder.content_max ? fillerText(placeholder.content_max) : placeholder.sample;
    })
    .join('');
}

/** The placeholders of a content the company has no value for yet. */
export function emptyPlaceholders(content: string, known: PlaceholderMap): Placeholder[] {
  return placeholderKeys(content, known)
    .map((key) => known.get(key)!)
    .filter((placeholder) => placeholder.has_value === false);
}

/** Whether a text prints: without placeholders always, else when one of them has a value. */
export function wouldPrint(content: string, known: PlaceholderMap): boolean {
  const keys = placeholderKeys(content, known);
  return keys.length === 0 || keys.some((key) => known.get(key)?.has_value !== false);
}

/**
 * What an element is called in messages and on its chip: a text that is one
 * placeholder (quotes allowed) carries that placeholder's label, any other text its
 * words with the samples filled in, cut short; other kinds their catalog label.
 */
export function titleOf(
  element: DesignElement,
  entry: CatalogEntry | undefined,
  known: PlaceholderMap,
): string {
  if (entry?.render !== 'text') return entry?.label ?? element.kind;
  const content = element.content ?? '';
  const keys = placeholderKeys(content, known);
  const bare = content
    .replace(/^[„"]\s*/, '')
    .replace(/\s*[“"]$/, '')
    .trim();
  if (keys.length === 1 && bare === `{${keys[0]}}`) return known.get(keys[0])!.label;
  const text = resolveSample(content, known).replace(/\s+/g, ' ').trim() || 'Text';
  return text.length > 24 ? text.slice(0, 23).trimEnd() + '…' : text;
}

/** The engine's kind of an element: a text wraps or stays one line (the backend's
 * `"multiline" if wrap else "text"`); every other kind is its catalog render. */
export function renderOf(element: DesignElement, entry: CatalogEntry | undefined): ElementRender {
  if (entry?.render === 'text' || !entry) return element.wrap ? 'multiline' : 'text';
  return entry.render;
}
